import { useCallback, useRef, useState } from "react";
import type { RioTrip, RioTripsFilters, RioTripsPage } from "@types";
import { getRioTrips } from "@services";
import { appendUniqueTrips } from "@utils/rioTripOptions";

export const RIO_TRIPS_PAGE_SIZE = 20;

const hasNextPage = (page: RioTripsPage) => page.content.length > 0 && page.currentPage < page.totalPages;

export const useRioTripsPagination = (pageSize: number = RIO_TRIPS_PAGE_SIZE) => {
  const [trips, setTrips] = useState<RioTrip[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const filtersRef = useRef<RioTripsFilters>({});
  const pageRef = useRef(0);
  const requestRef = useRef(0);
  const hasMoreRef = useRef(false);
  const busyRef = useRef(false);

  const updateHasMore = (value: boolean) => {
    hasMoreRef.current = value;
    setHasMore(value);
  };

  const fetchTrips = useCallback(
    async (filters: RioTripsFilters = {}) => {
      const requestId = ++requestRef.current;
      filtersRef.current = { ...filters };
      pageRef.current = 0;
      busyRef.current = true;
      updateHasMore(false);
      setIsLoadingMore(false);

      try {
        const page = await getRioTrips({ ...filters, page: 1, size: pageSize });
        if (requestId !== requestRef.current) return;
        pageRef.current = 1;
        setTrips(appendUniqueTrips([], page.content));
        updateHasMore(hasNextPage(page));
      } catch {
        if (requestId !== requestRef.current) return;
        setTrips([]);
      } finally {
        if (requestId === requestRef.current) busyRef.current = false;
      }
    },
    [pageSize]
  );

  const loadMoreTrips = useCallback(async () => {
    if (busyRef.current || !hasMoreRef.current) return;

    const requestId = requestRef.current;
    const nextPage = pageRef.current + 1;
    busyRef.current = true;
    setIsLoadingMore(true);

    try {
      const page = await getRioTrips({ ...filtersRef.current, page: nextPage, size: pageSize });
      if (requestId !== requestRef.current) return;
      pageRef.current = nextPage;
      setTrips((current) => appendUniqueTrips(current, page.content));
      updateHasMore(hasNextPage(page));
    } catch {
      if (requestId !== requestRef.current) return;
      updateHasMore(false);
    } finally {
      if (requestId === requestRef.current) {
        busyRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [pageSize]);

  return { trips, hasMore, isLoadingMore, fetchTrips, loadMoreTrips };
};
